import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatEUR, firstCategoryWithProducts } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Trash2, Receipt, FileText, Plus, Minus, AlertTriangle, Volume2, Ticket } from "lucide-react";
import ScontrinoPreview from "@/components/ScontrinoPreview";
import TicketPrint from "@/components/TicketPrint";
import InlinePOSKeypad from "@/components/pos/InlinePOSKeypad";
import GestionaleLongPress from "@/components/pos/GestionaleLongPress";
import PrecontoButton from "@/components/pos/PrecontoButton";
import { checkStockAlert } from "@/lib/stockAlert";
import { authorizeCardIfNeeded } from "@/lib/paymentPos";
import { newOfflineId } from "@/lib/offlineQueue";
import { recoverUnreachableSale, SERVER_ATTEMPT } from "@/lib/offlineSales";

export default function POSFesta() {
  const [tenant, setTenant] = useState(null);
  const [cats, setCats] = useState([]);
  const [prods, setProds] = useState([]);
  const [activeCat, setActiveCat] = useState(null);
  const [lines, setLines] = useState([]);
  const [closeMode, setCloseMode] = useState("non_fiscal"); // 'fiscal' | 'non_fiscal'
  const [showPay, setShowPay] = useState(false);
  const [showTickets, setShowTickets] = useState(false);
  const [lastClosedOrderId, setLastClosedOrderId] = useState(null);
  const [lastClosed, setLastClosed] = useState(null);
  const [showScontrino, setShowScontrino] = useState(false);
  const [pendingOrders, setPendingOrders] = useState([]);
  const [discountPct, setDiscountPct] = useState(0);

  useEffect(() => {
    api.get("/tenant/me").then((r) => setTenant(r.data));
    api.get("/categories?mode=festa").then((r) => setCats(Array.isArray(r.data) ? r.data : []));
    api.get("/products?mode=festa").then((r) => setProds(Array.isArray(r.data) ? r.data : []));
    api.get("/orders?mode=festa&limit=20").then((r) => setPendingOrders((Array.isArray(r.data) ? r.data : []).filter(o => o.status === "closed_non_fiscal").slice(0, 10)));
  }, []);

  useEffect(() => {
    if (activeCat == null && cats.length && prods.length) setActiveCat(firstCategoryWithProducts(cats, prods));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cats, prods]);

  const filteredProds = useMemo(() => (Array.isArray(prods) ? prods : []).filter((p) => p.category_id === activeCat && p.visible !== false), [prods, activeCat]);
  const subtotal = lines.reduce((s, l) => s + l.price * l.qty, 0);
  const total = discountPct > 0 ? subtotal * (1 - discountPct / 100) : subtotal;

  const addFreeLine = (amountOrObj) => {
    let name, price, vat;
    if (typeof amountOrObj === "number") {
      name = "Prezzo libero"; price = amountOrObj; vat = 22;
    } else {
      ({ name, price, vat } = amountOrObj);
    }
    setLines((prev) => [...prev, { product_id: null, name, qty: 1, price, vat }]);
    toast.success(`Aggiunto: ${name}`);
  };
  const applyDiscount = ({ mode, value }) => {
    const pct = mode === "pct" ? Math.min(100, Math.max(0, value)) : (subtotal > 0 ? Math.min(100, (value / subtotal) * 100) : 0);
    setDiscountPct(pct);
    toast.success(pct > 0 ? `Sconto ${pct.toFixed(1)}% applicato` : "Sconto rimosso");
  };
  const [precontoOrderId, setPrecontoOrderId] = useState(null);
  const saveOrderForGestionale = async () => {
    if (!lines.length) { toast.error("Nessun articolo"); return null; }
    try {
      if (precontoOrderId) {
        const r = await api.patch(`/orders/${precontoOrderId}`, { lines, discount_pct: discountPct || 0 });
        return r.data;
      }
      const r = await api.post("/orders", { mode: "festa", lines, discount_pct: discountPct || 0 });
      return r.data;
    } catch { toast.error("Errore"); return null; }
  };
  const onGestionaleClosed = () => { setLines([]); setDiscountPct(0); setPrecontoOrderId(null); };

  const lowStockItems = useMemo(() => (Array.isArray(prods) ? prods : []).filter(p => p.stock_limit && p.stock_current !== null && p.stock_current <= p.stock_limit).slice(0, 5), [prods]);

  const addProduct = (p) => {
    const idx = lines.findIndex(l => l.product_id === p.id);
    if (idx >= 0) {
      const upd = [...lines]; upd[idx] = { ...upd[idx], qty: upd[idx].qty + 1 }; setLines(upd);
    } else {
      setLines([...lines, { product_id: p.id, name: p.name, qty: 1, price: p.price, vat: p.vat, print_to: p.effective_print_to || p.print_to }]);
    }
    // Stock alert proiettato
    if (p.stock_current != null && p.stock_limit != null) {
      const inCart = (Array.isArray(lines) ? lines : []).filter(l => l.product_id === p.id).reduce((s, l) => s + (l.qty || 0), 0);
      checkStockAlert({ ...p, stock_current: p.stock_current - (inCart + 1) });
    }
  };
  const incLine = (i, d) => {
    const upd = [...lines]; upd[i] = { ...upd[i], qty: Math.max(0, upd[i].qty + d) };
    if (upd[i].qty === 0) upd.splice(i, 1);
    setLines(upd);
  };

  const close = async (method) => {
    if (!lines.length) return;
    if (closeMode === "fiscal") {
      const pos = await authorizeCardIfNeeded({ method, amount: total });
      if (!pos.ok) { toast.error(pos.message || "Pagamento carta non autorizzato"); return; }
      if (pos.mode === "pos") toast.success("Carta autorizzata sul POS");
    }
    const offlineId = newOfflineId();
    let orderId = precontoOrderId || null;
    try {
      let order;
      if (precontoOrderId) {
        const r = await api.patch(`/orders/${precontoOrderId}`, { lines, discount_pct: discountPct || 0 }, SERVER_ATTEMPT);
        order = r.data;
      } else {
        const r = await api.post("/orders", { mode: "festa", lines, payment_method: method, discount_pct: discountPct || 0, offline_id: offlineId }, SERVER_ATTEMPT);
        order = r.data;
      }
      orderId = order.id;
      if (closeMode === "fiscal") {
        const c = await api.post(`/orders/${order.id}/close-fiscal`, { payment_method: method, discount_pct: discountPct || 0 }, SERVER_ATTEMPT);
        setLastClosed(c.data);
        toast.success(`Scontrino #${c.data.receipt_number} emesso`);
      } else {
        const c = await api.post(`/orders/${order.id}/close-non-fiscal`, { payment_method: method }, SERVER_ATTEMPT);
        setLastClosed(c.data);
        toast.success(`Ordine N. ${c.data.order_number}`);
        setPendingOrders((p) => [c.data, ...p].slice(0, 10));
      }
      setShowPay(false);
      setShowScontrino(true);
      setLastClosedOrderId(order.id);
      setLines([]); setDiscountPct(0); setPrecontoOrderId(null);
    } catch (e) {
      if (closeMode === "fiscal") {
        const off = await recoverUnreachableSale(e, {
          tenant, offline_id: offlineId, server_order_id: orderId, mode: "festa", lines,
          payment_method: method, discount_pct: discountPct || 0,
        });
        if (off.printed) {
          setLastClosed(off.order);
          setShowPay(false);
          setShowScontrino(true);
          setLines([]); setDiscountPct(0); setPrecontoOrderId(null);
          return;
        }
        if (off.handled) return;
      }
      toast.error("Errore");
    }
  };

  const callOrder = (order) => {
    toast.success(`📢 Ordine ${order.order_number} è pronto!`);
    if ('speechSynthesis' in window) {
      const u = new SpeechSynthesisUtterance(`Ordine numero ${order.order_number}, prego ritirare`);
      u.lang = 'it-IT';
      window.speechSynthesis.speak(u);
    }
  };

  return (
    <div className="h-screen bg-velta-bg flex flex-col overflow-hidden">
      <header className="h-14 border-b border-velta-border bg-white flex items-center px-4 gap-4 shrink-0">
        <Link to="/cassa" className="inline-flex items-center gap-2 text-velta-mute hover:text-velta-ink text-sm font-medium">
          <ArrowLeft className="w-4 h-4" /> Modalità
        </Link>
        <div className="h-6 w-px bg-velta-border" />
        <div className="font-heading font-semibold tracking-tight">Velta One · Festa di paese</div>
        <div className="ml-auto inline-flex items-center gap-2">
          <span className="text-xs font-mono uppercase tracking-widest text-velta-mute">Modalità chiusura:</span>
          <div className="inline-flex border border-velta-border rounded-md overflow-hidden">
            <button data-testid="festa-mode-non-fiscal" onClick={() => setCloseMode("non_fiscal")} className={`px-3 h-9 text-xs font-medium ${closeMode === "non_fiscal" ? "bg-velta-ink text-white" : "bg-white text-velta-ink"}`}>Non Fiscale</button>
            <button data-testid="festa-mode-fiscal" onClick={() => setCloseMode("fiscal")} className={`px-3 h-9 text-xs font-medium ${closeMode === "fiscal" ? "bg-velta-ink text-white" : "bg-white text-velta-ink"}`}>Fiscale</button>
          </div>
        </div>
      </header>

      {lowStockItems.length > 0 && (
        <div className="bg-yellow-50 border-b border-yellow-200 px-4 py-2 flex items-center gap-3 text-sm">
          <AlertTriangle className="w-4 h-4 text-yellow-700 shrink-0" />
          <div className="flex-1 text-yellow-900">Scorte basse: {(Array.isArray(lowStockItems) ? lowStockItems : []).map(p => `${p.name} (${p.stock_current})`).join(", ")}</div>
        </div>
      )}

      <div className="flex-1 grid grid-cols-12 overflow-hidden">
        <section className="col-span-7 flex flex-col overflow-hidden">
          <div className="h-20 border-b border-velta-border bg-white flex gap-2 overflow-x-auto pos-scroll p-3">
            {(Array.isArray(cats) ? cats : []).map((c) => (
              <button key={c.id} data-testid={`festa-cat-${c.id}`} onClick={() => setActiveCat(c.id)}
                className={`shrink-0 h-14 px-5 rounded-md font-medium btn-tactile flex items-center gap-2 ${activeCat === c.id ? "text-white" : "bg-white border border-velta-border text-velta-ink hover:border-velta-ink"}`}
                style={activeCat === c.id ? { background: c.color } : {}}>
                <div className="w-2 h-2 rounded-sm" style={{ background: activeCat === c.id ? "rgba(255,255,255,0.5)" : c.color }} />
                {c.name}
              </button>
            ))}
          </div>
          <div className="flex-1 overflow-auto pos-scroll p-3">
            <div className="grid grid-cols-3 lg:grid-cols-4 gap-3">
              {(Array.isArray(filteredProds) ? filteredProds : []).map(p => {
                const cat = cats.find(c => c.id === p.category_id);
                const color = p.color || cat?.color || "#0A0A0A";
                return (
                  <button key={p.id} data-testid={`festa-prod-${p.id}`} onClick={() => addProduct(p)} className="relative bg-white border border-velta-border rounded-md p-4 h-28 text-left flex flex-col justify-between hover:border-velta-ink btn-tactile">
                    <div className="absolute top-0 left-0 right-0 h-1.5" style={{ background: color }} />
                    <div className="font-medium text-sm mt-2">{p.name}</div>
                    {p.show_price && <div className="font-mono font-bold text-lg">{formatEUR(p.price)}</div>}
                  </button>
                );
              })}
            </div>
          </div>
        </section>

        <aside className="col-span-3 border-l border-r border-velta-border bg-white flex flex-col overflow-hidden">
          <div className="p-4 border-b border-velta-border">
            <div className="text-[10px] font-mono uppercase tracking-widest text-velta-mute">Ordine corrente</div>
            <h2 className="font-heading font-semibold text-lg">Carrello</h2>
          </div>
          <div className="flex-1 overflow-auto pos-scroll p-3">
            {lines.length === 0 ? <div className="text-center text-sm text-velta-mute p-8">Aggiungi articoli.</div> : (
              <div className="space-y-2">
                {(Array.isArray(lines) ? lines : []).map((l, i) => (
                  <div key={i} className="border border-velta-border rounded-md p-3">
                    <div className="flex items-start justify-between mb-2">
                      <div className="font-medium text-sm">{l.name}</div>
                      <button onClick={() => incLine(i, -l.qty)} className="text-velta-error"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="inline-flex items-center border border-velta-border rounded-md">
                        <button onClick={() => incLine(i, -1)} className="w-8 h-8 flex items-center justify-center"><Minus className="w-3.5 h-3.5" /></button>
                        <span className="w-8 text-center font-mono font-medium">{l.qty}</span>
                        <button onClick={() => incLine(i, 1)} className="w-8 h-8 flex items-center justify-center"><Plus className="w-3.5 h-3.5" /></button>
                      </div>
                      <div className="font-mono font-bold">{formatEUR(l.price * l.qty)}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="border-t border-velta-border p-4 bg-velta-bg">
            <GestionaleLongPress orderId={null} total={total} saveOrder={saveOrderForGestionale} onClosed={onGestionaleClosed}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-mono uppercase tracking-widest text-velta-mute">Totale</span>
                <span data-testid="festa-total" className="font-mono font-bold text-3xl tracking-tighter">{formatEUR(total)}</span>
              </div>
              {discountPct > 0 && (
                <div className="text-[10px] font-mono text-velta-success text-right mb-1">
                  Sconto -{discountPct.toFixed(1)}% (subtot. {formatEUR(subtotal)})
                </div>
              )}
            </GestionaleLongPress>
            <div className="mb-2">
              <InlinePOSKeypad onAddFreePrice={addFreeLine} onApplyDiscount={applyDiscount} currentTotal={total} compact />
            </div>
            <PrecontoButton mode="festa" lines={lines} discountPct={discountPct} tenant={tenant} orderId={precontoOrderId} onOrderSaved={setPrecontoOrderId} testid="festa-preconto-btn" />
            <button data-testid="festa-pay-btn" disabled={!lines.length} onClick={() => setShowPay(true)} className="w-full h-14 bg-[#1E2D5B] hover:bg-[#162244] text-white rounded-md font-medium text-lg flex items-center justify-center gap-2 btn-tactile disabled:opacity-40">
              {closeMode === "fiscal" ? <><Receipt className="w-5 h-5" /> Scontrino Fiscale</> : <><FileText className="w-5 h-5" /> Stampa N. Ordine</>}
            </button>
            {lastClosedOrderId && (
              <button
                data-testid="festa-tickets-btn"
                onClick={() => setShowTickets(true)}
                className="w-full mt-2 h-12 bg-[#2F9E77] hover:bg-[#27855F] text-white rounded-md font-medium inline-flex items-center justify-center gap-2 btn-tactile"
              >
                <Ticket className="w-4 h-4" /> Stampa tagliandini ultimo ordine
              </button>
            )}
          </div>
        </aside>

        <aside className="col-span-2 bg-velta-ink text-white flex flex-col overflow-hidden">
          <div className="p-4 border-b border-white/10">
            <div className="text-[10px] font-mono uppercase tracking-widest text-white/50">Display chiama-numero</div>
            <h2 className="font-heading font-semibold text-lg">Ordini pronti</h2>
          </div>
          <div className="flex-1 overflow-auto pos-scroll p-3 space-y-2">
            {pendingOrders.length === 0 && <div className="text-center text-sm text-white/40 p-8">Nessun ordine in coda.</div>}
            {(Array.isArray(pendingOrders) ? pendingOrders : []).map(o => (
              <button key={o.id} onClick={() => callOrder(o)} className="w-full bg-white/5 hover:bg-[#1E2D5B] border border-white/10 rounded-md p-3 text-left transition-colors">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-[10px] font-mono uppercase tracking-widest text-white/50">N. Ordine</div>
                    <div className="font-heading font-bold text-3xl">{o.order_number}</div>
                  </div>
                  <Volume2 className="w-5 h-5 text-white/60" />
                </div>
                <div className="text-xs text-white/60 mt-1 font-mono">{formatEUR(o.total)}</div>
              </button>
            ))}
          </div>
        </aside>
      </div>

      {showPay && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50" onClick={() => setShowPay(false)}>
          <div className="bg-white rounded-md w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-heading text-2xl font-semibold mb-2">Pagamento</h3>
            <div className="bg-velta-bg border border-velta-border rounded-md p-4 mb-4 text-center">
              <div className="font-mono font-bold text-3xl">{formatEUR(total)}</div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <button data-testid="festa-pay-cash" onClick={() => close("cash")} className="h-14 bg-velta-ink text-white rounded-md font-medium btn-tactile">Contanti</button>
              <button data-testid="festa-pay-card" onClick={() => close("card")} className="h-14 bg-[#1E2D5B] text-white rounded-md font-medium btn-tactile">Carta</button>
            </div>
          </div>
        </div>
      )}
      {showScontrino && <ScontrinoPreview order={lastClosed} tenant={tenant} fiscal={lastClosed?.receipt_type === "fiscal"} onClose={() => setShowScontrino(false)} />}
      {showTickets && lastClosedOrderId && <TicketPrint orderId={lastClosedOrderId} onClose={() => setShowTickets(false)} />}
    </div>
  );
}
